import { useEffect } from 'react'
import { readPaper } from '@chegg-wt/core-wa/utils/paperIndexedDb'
import { useRouter } from 'next/router'
import { batch, useDispatch, useSelector } from 'react-redux'
import { isLoggingOutSelector, paperIdSelector } from '@chegg-wt/core-wa/state/selectors'
import {
  adjustSentenceOffsets,
  setFeedbacks,
  setPaperId,
  setPaperRequiresRecheck,
  setPaperTitle,
  setTotalFeedbackCount,
  setWordCount,
  setWritingAssitantPaperStatus,
} from '@chegg-wt/core-wa/state/actions'
import { navigated } from '@chegg-wt/core/state/app/actions'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import getFeatureEditor from '@chegg-wt/core-wa/utils/getEditor'
import { getWordCount } from '@chegg-wt/core-wa/utils/helpers'
import {
  getLocalStorageItem,
  LOCAL_STORAGE_KEYS,
} from '@chegg-wt/core/utils/localStorage'
import { loggedInSelector } from '@chegg-wt/core/state/user/selectors'

export const useUpdateStateFromDB = () => {
  const editor = getFeatureEditor()

  const router = useRouter()
  const dispatch = useDispatch()
  const routes = useRoutesContext()
  const paperId = useSelector(paperIdSelector)
  const isLoggedInRedux = useSelector(loggedInSelector)
  // We are depending on the user state from local storage isntead of redux because redux state clears up on refresh
  // and there is a delay before its updated again causing unexpected behaviour.

  // TODO: remove below loggedIn, since logout issue is fixed.
  const userInfo = JSON.parse(getLocalStorageItem(LOCAL_STORAGE_KEYS.user))
  const isLoggedIn = !!(userInfo && userInfo.user && userInfo.user.email)
  const isLoggingOut = useSelector(isLoggingOutSelector)

  useEffect(() => {
    if (editor && router.query.id) {
      const readPaperFromDb = async () => {
        try {
          if (paperId === router.query.id) return
          // To Do: Remove these comments below if we do not use this fallback case.
          // logged in state takes time in fetching the data on reload however roles persists.
          // Since the value of signed in user can be myBib or myBibPro only we can consider it
          // as equal to isLoggedIn. otherwise the use we get rediected to flash paper.
          // const isSignedInRole =
          //  userRole === UserRoles.FREE_USER || userRole === UserRoles.PRO_USER

          const paperFromDb = await readPaper(
            router.query.id,
            isLoggedIn || isLoggedInRedux
          )

          // paper not found
          if (!paperFromDb) {
            dispatch(navigated(routes.toWritingAssistant()))
          }
          const timestamp = new Date().getTime()
          // This command will cause the review button to be enabled as we are not
          // setting IS_NON_MANUAL_TEXT_UPDATE meta data as true for this command
          editor.commands.setContent(paperFromDb.paperContent.editorJSON)
          // Remove old highlights saved in editor
          editor.commands.clearAllHighlights()

          batch(() => {
            dispatch(setPaperId(paperFromDb.id))
            dispatch(setPaperTitle(paperFromDb.title))
            dispatch(setWritingAssitantPaperStatus(paperFromDb.status))
            dispatch(setFeedbacks(paperFromDb.feedbackState.feedbackList))
            dispatch(setTotalFeedbackCount(paperFromDb.feedbackState.totalFeedbackCount))
            dispatch(setPaperRequiresRecheck(paperFromDb.requiresRecheck))
          })

          const editorText = editor.getText()
          dispatch(setWordCount(getWordCount(editorText)))
          const sentences = paperFromDb.feedbackState.sentences.map((sentence) => {
            // -1 as editor offsets are 1 based
            const originalText = editorText.substring(
              sentence.offset - 1,
              sentence.offset + sentence.length - 1
            )
            return {
              originalText,
              lastUpdated: timestamp,
              ...sentence,
            }
          })
          dispatch(adjustSentenceOffsets(sentences))
        } catch {
          // Some issue in parsing paper data, delete and move to draft
          // pass
        }
      }
      if (!isLoggingOut) readPaperFromDb()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.id, editor, isLoggedIn])
}
